import type { ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button, type ButtonProps } from '@/components/ui/button'

/**
 * Modal - replaces the legacy frontend/public/js/modal.js BoKapModal.
 * Built on shadcn Dialog so children pass through JSX (auto-escaped),
 * eliminating the innerHTML XSS surface in the old implementation.
 *
 * Three usage shapes:
 *  1. Controlled: <Modal open onOpenChange title="…">{body}</Modal>
 *  2. Confirm:    <Modal.Confirm open … onConfirm onCancel/>
 *  3. Alert:      <Modal.Alert open … onClose/>
 */

interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * String or JSX. JSX lets callers inline a HelpTooltip / badge next to
   * the heading without having to extend Modal each time.
   */
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  className?: string
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={className}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children ? (
          <div className="flex-1 overflow-auto px-6 py-4 text-sm">
            {children}
          </div>
        ) : null}
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  )
}

interface ConfirmProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmVariant?: ButtonProps['variant']
  onConfirm: () => void | Promise<void>
  onCancel?: () => void
  busy?: boolean
}

function Confirm({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = 'Yes, proceed',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
  busy,
}: ConfirmProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              onCancel?.()
              onOpenChange(false)
            }}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={() => void onConfirm()}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  )
}

interface AlertProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  body?: ReactNode
  closeLabel?: string
  onClose?: () => void
}

function AlertModal({ open, onOpenChange, title, body, closeLabel = 'OK', onClose }: AlertProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <Button
          onClick={() => {
            onClose?.()
            onOpenChange(false)
          }}
        >
          {closeLabel}
        </Button>
      }
    >
      {body}
    </Modal>
  )
}

Modal.Confirm = Confirm
Modal.Alert = AlertModal
